import * as Sentry from '@sentry/react'
import posthog from 'posthog-js'

// Environment detection
const isDevelopment = import.meta.env.DEV
const isProduction = import.meta.env.PROD

// Sentry Configuration
export function initSentry() {
  if (isProduction || isDevelopment) {
    Sentry.init({
      dsn: import.meta.env.VITE_SENTRY_DSN,
      environment: import.meta.env.MODE,
      sendDefaultPii: false, // Better for privacy
      integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.replayIntegration({
          maskAllText: false,
          maskAllInputs: true,
          blockAllMedia: false,
        }),
        Sentry.browserProfilingIntegration(),
      ],
      // Tracing - reduce for production
      tracesSampleRate: isProduction ? 0.1 : 1.0,
      profilesSampleRate: isProduction ? 0.1 : 1.0,
      // Configure specific targets for your API
      tracePropagationTargets: [
        'localhost',
        /^https:\/\/.*\.supabase\.co\/rest/,
        /^https:\/\/.*\.netlify\.app/,
      ],
      // Session Replay
      replaysSessionSampleRate: isProduction ? 0.1 : 0.5,
      replaysOnErrorSampleRate: 1.0,
      // Logs
      enableLogs: true,
      // Release tracking
      release: import.meta.env.VITE_APP_VERSION || `${Date.now()}`,
      // Filters and context
      beforeSend(event, hint) {
        // Ignore temporary network errors
        const error = hint.originalException
        if (
          typeof error === 'object' &&
          error !== null &&
          'name' in error &&
          error.name === 'NetworkError'
        ) {
          return null
        }

        // Ignore browser extension errors
        if (
          event.exception?.values?.[0]?.stacktrace?.frames?.some(
            (frame) =>
              frame.filename?.includes('chrome-extension://') ||
              frame.filename?.includes('moz-extension://')
          )
        ) {
          return null
        }

        // Add custom context
        event.contexts = {
          ...event.contexts,
          app: {
            build_time: new Date().toISOString(),
            feature_flags: window.posthog?.getFeatureFlags?.() || {},
          },
        }

        if (isDevelopment) {
          console.log('Sentry event captured:', event)
        }

        return event
      },
      // Ignore specific errors
      ignoreErrors: [
        'ResizeObserver loop limit exceeded',
        'ResizeObserver loop completed with undelivered notifications',
        'Non-Error promise rejection captured',
      ],
    })
  }
}

// PostHog Configuration
export function initPostHog() {
  if (isProduction || isDevelopment) {
    posthog.init(import.meta.env.VITE_PUBLIC_POSTHOG_KEY, {
      api_host: import.meta.env.VITE_PUBLIC_POSTHOG_HOST,
      // Debug in development
      loaded: (posthog) => {
        if (isDevelopment) posthog.debug()
        // Expose PostHog to window for hooks (with proper typing)
        window.posthog = posthog as Window['posthog']
      },
      // Privacy-friendly settings
      capture_pageview: true,
      capture_pageleave: true,
      disable_session_recording: false,
      // Optimized autocapture
      autocapture: {
        dom_event_allowlist: ['click', 'submit', 'change'],
        element_allowlist: ['button', 'a', 'input', 'select'],
        css_selector_allowlist: ['[data-track]'], // Elements with data-track will be tracked
      },
      // Optimized session recording
      session_recording: {
        maskAllInputs: false,
        maskInputOptions: {
          password: true,
          email: true,
          tel: true,
        },
        recordCrossOriginIframes: false,
        maskTextSelector: '.sensitive-data',
      },
      // Performance
      bootstrap: {
        featureFlags: {}, // Initial feature flags if needed
      },
      opt_out_capturing_by_default: false,
      // Cookies and persistence
      persistence: 'localStorage+cookie',
      cross_subdomain_cookie: false,
    })

    // Expose PostHog to window immediately for hooks to access
    window.posthog = posthog as Window['posthog']
  }
}

// Analytics Event Types for type safety
export interface AnalyticsEvents {
  // Book Management
  book_added: {
    method: 'manual' | 'isbn' | 'barcode'
    genre_count: number
    has_cover: boolean
    status: string
  }
  book_updated: {
    changed_fields: string[]
    genre_count: number
  }
  book_deleted: {
    genre_count: number
    status: string
    had_rating: boolean
  }

  // ISBN & Barcode
  isbn_lookup_success: {
    provider: string
    response_time_ms: number
  }
  isbn_lookup_failed: {
    error_type: string
    isbn: string
  }
  barcode_scan_success: {
    scan_time_ms: number
  }
  barcode_scan_failed: {
    error_type: string
  }

  // Roulette
  roulette_spin: {
    book_count: number
    result_book_id: string
  }
  roulette_created: {
    book_count: number
  }

  // Filters & Search
  filter_applied: {
    filter_type: string
    value_count: number
  }
  search_performed: {
    query_length: number
    results_count: number
  }

  // User Journey
  page_viewed: {
    page: string
    book_count?: number
  }
  feature_used: {
    feature: string
    context?: string
  }
}

// Type-safe analytics wrapper
export function trackEvent<K extends keyof AnalyticsEvents>(
  event: K,
  properties: AnalyticsEvents[K]
) {
  if (typeof window !== 'undefined' && window.posthog) {
    window.posthog.capture(event, properties)
  }
}

// Error tracking helpers
export function captureError(error: Error, context?: Record<string, unknown>) {
  console.error('Error captured:', error)

  if (isProduction) {
    Sentry.captureException(error, {
      extra: context,
    })
  }
}

export function captureMessage(message: string, level: 'info' | 'warning' | 'error' = 'info') {
  console.log(`[${level.toUpperCase()}] ${message}`)

  if (isProduction) {
    Sentry.captureMessage(message, level)
  }
}

// User identification (for authenticated users)
export function identifyUser(userId: string, traits?: Record<string, unknown>) {
  if (typeof window !== 'undefined' && window.posthog) {
    window.posthog.identify(userId, traits)
  }

  if (isProduction) {
    Sentry.setUser({
      id: userId,
      ...traits,
    })
  }
}

// Performance measurement helper
export function measurePerformance<T>(operation: string, fn: () => Promise<T>): Promise<T> {
  const startTime = performance.now()

  return fn()
    .then((result) => {
      const duration = performance.now() - startTime
      trackEvent('feature_used', {
        feature: operation,
        context: `${duration.toFixed(2)}ms`,
      })
      return result
    })
    .catch((error) => {
      const duration = performance.now() - startTime
      captureError(error, {
        operation,
        duration_ms: duration,
      })
      throw error
    })
}

// Feature flag helpers
export function isFeatureEnabled(feature: string): boolean {
  if (typeof window !== 'undefined' && window.posthog) {
    return window.posthog.isFeatureEnabled(feature) ?? false
  }
  return false
}

export function getFeatureFlags(): Record<string, boolean | string> {
  if (typeof window !== 'undefined' && window.posthog) {
    return window.posthog.getFeatureFlags() || {}
  }
  return {}
}

// Initialize everything
export function initMonitoring() {
  initSentry()
  initPostHog()

  console.log('📊 Monitoring initialized:', {
    sentry: isProduction,
    posthog: isProduction || isDevelopment,
    environment: import.meta.env.MODE,
  })
}
